import { useState } from "react";
import { createContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  changeUserConfirmAdminService,
  changeUserPasswordAdminService,
  createUserAdminService,
  deleteUserAdminService,
  editUserAdminService,
  getUsersAdminService,
  getUserTokenAdminService,
  verifyUserEmailAdminService,
  verifyUserPhoneNumberAdminService,
} from "./../../../services/adminServices";
import {
  deliverySuccessMessage,
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { setAuthAction } from "../../../redux/actions/authActions/authActions";

export const userAdminContext = createContext({
  users: [],
  setUsers: () => {},
  user: {},
  setUser: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  isConfirm: "",
  setIsConfirm: () => {},
  role: "",
  setRole: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetUsers: () => {},
  handleCreateUser: () => {},
  handleEditUser: () => {},
  handleChangeUserPassword: () => {},
  handleChangeUserConfirm: () => {},
  handleDeleteUser: () => {},
  handleCreateTransaction: () => {},
  handleVerifyUserPhoneNumber: () => {},
  handleVerifyUserEmail: () => {},
  handleGetUserToken: () => {},
});

const UserAdminContext = ({ children }) => {
  const dispatch = useDispatch();

  const auth = useSelector((state) => state.auth);

  const [users, setUsers] = useState([]);
  const [user, setUser] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [role, setRole] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [isConfirm, setIsConfirm] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setUser(val);
      setValue("avatar", val.avatar);
      setValue("firstName", val.firstName);
      setValue("lastName", val.lastName);
      setValue("phoneNumber", val.phoneNumber);
      setValue("nationalNumber", val.nationalNumber);
      setValue("email", val.email);
      setValue("address", val.address);
      setValue("role", val.role);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setUser({});
  };

  // Get Users
  const handleGetUsers = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      ...(role && { role }),
      ...(isConfirm && { isConfirm }),
      filter,
      pageNumber,
      sort,
      itemsCount,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        setUsers(data.result?.users);
        setPageCount(data?.result?.allPagesCount);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create User
  const handleCreateUser = async (userData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createUserAdminService(userData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit User
  const handleEditUser = async (userData) => {
    const objData = {
      id: user._id,
      ...userData,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editUserAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Change User Password
  const handleChangeUserPassword = async (userData) => {
    const objData = {
      id: user._id,
      ...userData,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeUserPasswordAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        setUser({});
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Change User Confirm
  const handleChangeUserConfirm = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeUserConfirmAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete User
  const handleDeleteUser = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteUserAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Verify User PhoneNumber
  const handleVerifyUserPhoneNumber = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await verifyUserPhoneNumberAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Verify User Email
  const handleVerifyUserEmail = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await verifyUserEmailAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Get User Token
  const handleGetUserToken = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUserTokenAdminService(id);
      if (status === 200) {
        localStorage.setItem("adminToken", auth.accessToken);
        dispatch(setAuthAction(data.result?.accessToken));
        deliverySuccessMessage(data.resultMessage, "/", "3000");
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <userAdminContext.Provider
      value={{
        users,
        setUsers,
        user,
        setUser,
        filter,
        setFilter,
        sort,
        setSort,
        role,
        setRole,
        start,
        setStart,
        end,
        setEnd,
        isConfirm,
        setIsConfirm,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetUsers,
        handleCreateUser,
        handleEditUser,
        handleChangeUserPassword,
        handleChangeUserConfirm,
        handleDeleteUser,
        handleVerifyUserPhoneNumber,
        handleVerifyUserEmail,
        handleGetUserToken,
      }}
    >
      {children}
    </userAdminContext.Provider>
  );
};

export default UserAdminContext;
